import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ArticleCard from '../components/ArticleCard';
import api from '../api';

const NewsPage = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchSymbol, setSearchSymbol] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearch, setLastSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null); // null, 'india', or 'international'

  // Fetch news articles from the backend
  const fetchNews = async (symbols = '', limit = 50, category = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (symbols) params.append('symbols', symbols);
      if (category) params.append('category', category);
      if (limit) params.append('limit', limit.toString());
      
      const response = await api.get(`/api/v1/news/?${params}`);
      setArticles(response.data);
      
      if (symbols) {
        setLastSearch(symbols);
      }
      
      // console.log(`✅ Fetched ${response.data.length} news articles${category ? ` for ${category} category` : ''}`);
    } catch (error) {
      console.error('❌ Error fetching news:', error);
      
      let errorMessage = 'Failed to fetch news articles. Please try again later.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment before trying again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'News service temporarily unavailable. Please try again later.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  };

  // Handle category tab selection
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSearchSymbol(''); // Clear search when switching categories
    setLastSearch('');
    fetchNews('', 50, category);
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchSymbol.trim()) {
      setIsSearching(true);
      // Send raw user input to backend - backend will handle exchange suffixes
      fetchNews(searchSymbol.trim(), 50, activeCategory);
    }
  };

  // Handle clearing search
  const handleClearSearch = () => {
    setSearchSymbol('');
    setLastSearch('');
    fetchNews('', 50, activeCategory);
  };

  // Fetch news on component mount
  useEffect(() => {
    fetchNews();
  }, []);

  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <span className="ml-3 text-gray-600">Loading latest news...</span>
    </div>
  );

  // Error message component
  const ErrorMessage = ({ message }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <div className="text-red-600 mb-2">
        <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-red-800 mb-2">Unable to Load News</h3>
      <p className="text-red-700 mb-4">{message}</p>
      <button
        onClick={() => fetchNews()}
        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  // No articles found component
  const NoArticlesFound = () => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
      <div className="text-gray-400 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">No News Found</h3>
      <p className="text-gray-600 mb-4">
        {lastSearch 
          ? `No news articles found for "${lastSearch}". Try a different symbol or check back later.`
          : 'No news articles are currently available. Please check back later.'
        }
      </p>
      {lastSearch && (
        <button
          onClick={handleClearSearch}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          View All News
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <main className="flex-grow max-w-7xl mx-auto px-4 py-8">
        {/* Page Header with Category Tabs */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Latest Financial News & Updates
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Stay informed with the latest market news, company updates, and financial insights from around the world.
          </p>
          
          {/* Category Tabs */}
          <div className="flex justify-center">
            <div className="bg-gray-100 rounded-lg p-1 inline-flex">
              <button
                onClick={() => handleCategoryChange(null)}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                  activeCategory === null
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All News
              </button>
              <button
                onClick={() => handleCategoryChange('india')}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                  activeCategory === 'india'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                India
              </button>
              <button
                onClick={() => handleCategoryChange('international')}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 ${
                  activeCategory === 'international'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                International
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="searchSymbol" className="block text-sm font-medium text-gray-700 mb-2">
                  Search by Stock Symbol
                </label>
                <input
                  type="text"
                  id="searchSymbol"
                  value={searchSymbol}
                  onChange={(e) => setSearchSymbol(e.target.value)}
                  placeholder="e.g., AAPL, MSFT, RELIANCE, TCS"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Just type the stock symbol - we'll automatically add the correct exchange suffix
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  disabled={isSearching || !searchSymbol.trim()}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
                >
                  {isSearching ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching...
                    </span>
                  ) : (
                    'Search'
                  )}
                </button>
                {lastSearch && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Search Results Header */}
        {(lastSearch || activeCategory) && (
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-gray-900">
                {lastSearch ? (
                  <>News for: <span className="text-blue-600">{lastSearch}</span></>
                ) : (
                  <>
                    {activeCategory === 'india' && 'Indian Market News'}
                    {activeCategory === 'international' && 'International Market News'}
                    {activeCategory === null && 'All Financial News'}
                  </>
                )}
              </h2>
              <span className="text-sm text-gray-500">
                {articles.length} article{articles.length !== 1 ? 's' : ''} found
              </span>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="space-y-6">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage message={error} />
          ) : articles.length === 0 ? (
            <NoArticlesFound />
          ) : (
            <>
              {/* Articles Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {articles.map((article, index) => (
                  <ArticleCard key={index} article={article} />
                ))}
              </div>
              
              {/* Load More Button (if needed) */}
              {articles.length >= 50 && (
                <div className="text-center pt-6">
                  <button
                    onClick={() => fetchNews(lastSearch, 100, activeCategory)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                  >
                    Load More Articles
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* User Info (for debugging) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-8 p-4 bg-gray-100 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Debug Info:</strong> Logged in as {user?.email || 'Unknown'} 
              | Articles loaded: {articles.length} | Last search: {lastSearch || 'None'}
              | Active category: {activeCategory || 'All'}
            </p>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default NewsPage;
